import type { BarracksSoldier, BarracksState } from "@/types/barracks.types"
import type { Tower } from "@/types/tower.types"
import type { Enemy } from "@/types/enemy.types"
import type { Position } from "@/types/game.types"
import { generateId } from "@/utils/id-generator"
import { distanceSquared } from "@/utils/collision"
import { moveTowards, calculateAngle } from "@/utils/pathfinding"
import {
  getFootmanConfig,
  RESPAWN_TIME_MS,
  RALLY_RADIUS,
  ATTACK_RANGE,
  ATTACK_SPEED,
  DEFAULT_RALLY_OFFSET_Y,
  SOLDIER_NAMES,
} from "@/data/barracks-config"
import { getEmptyShrineBuffs } from "./shrine-system"
import { gameNow } from "@/utils/game-clock"

/**
 * Create initial barracks state with default rally point
 */
export function createBarracksState(towerPosition: Position): BarracksState {
  return {
    rallyPoint: {
      x: towerPosition.x,
      y: towerPosition.y + DEFAULT_RALLY_OFFSET_Y,
    },
    soldierIds: [], // Will be filled when soldiers are created
  }
}

/**
 * Create a barracks soldier at the rally point
 */
export function createBarracksSoldier(
  barracksTower: Tower,
  soldierIndex: number,
  soldierHpModifier: number = 1,
): BarracksSoldier {
  const config = getFootmanConfig(barracksTower.level)
  const rallyPoint = barracksTower.barracksState?.rallyPoint ?? {
    x: barracksTower.position.x,
    y: barracksTower.position.y + DEFAULT_RALLY_OFFSET_Y,
  }

  // Offset soldiers slightly from each other at rally point
  // Index 0: left, Index 1: right, Index 2: center (for reinforcements upgrade)
  const offsetX = soldierIndex === 0 ? -15 : soldierIndex === 1 ? 15 : 0
  const spawnPosition: Position = {
    x: rallyPoint.x + offsetX,
    y: rallyPoint.y,
  }

  const now = gameNow()

  // Apply HP modifier from Hardened Veterans upgrade
  const modifiedHealth = Math.floor(config.health * soldierHpModifier)

  // Randomly assign a medieval fantasy name
  const randomName = SOLDIER_NAMES[Math.floor(Math.random() * SOLDIER_NAMES.length)]

  return {
    id: generateId("soldier"),
    barracksTowerId: barracksTower.id,
    soldierIndex,
    name: randomName,
    level: barracksTower.level,
    position: spawnPosition,
    health: modifiedHealth,
    maxHealth: modifiedHealth,
    damage: config.damage,
    armor: config.armor,
    defenseType: "light", // Default defense type
    attackRange: ATTACK_RANGE,
    attackSpeed: ATTACK_SPEED,
    attackCooldown: 0,
    speed: config.speed,
    rotation: 0,
    target: null,
    isDead: false,
    deathTime: null,
    respawnTime: null,
    spawnTime: now,
    lastDamageTime: 0, // No damage received yet
    animationState: "idle",
    animationFrame: 0,
    animationStartTime: now,
    facingDirection: "down",
    // Attack speed debuff (from elite rat's disease carrier ability)
    attackSpeedDebuff: 1, // No debuff initially
    attackSpeedDebuffEndFrame: 0,
    // Movement slow (from elite mushroom's spore cloud)
    slowMultiplier: 1, // No slow initially
    slowEndFrame: 0,
    forceRallyUntil: 0, // Not forced to rally initially
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

/**
 * Initialize barracks tower with soldiers
 * Returns the tower with updated barracksState and the new soldiers
 * @param soldierCount - Number of soldiers to spawn (2 base + reinforcements upgrade)
 * @param soldierHpModifier - HP modifier from Hardened Veterans upgrade
 */
export function initializeBarracks(
  tower: Tower,
  soldierCount: number = 2,
  soldierHpModifier: number = 1,
): { tower: Tower; soldiers: BarracksSoldier[] } {
  const barracksState = createBarracksState(tower.position)

  // Create the tower with initial barracks state (without soldier IDs yet)
  const towerWithState: Tower = {
    ...tower,
    barracksState,
  }

  // Create soldiers
  const soldiers: BarracksSoldier[] = []
  for (let i = 0; i < soldierCount; i++) {
    soldiers.push(createBarracksSoldier(towerWithState, i, soldierHpModifier))
  }

  // Update the barracks state with soldier IDs
  const finalTower: Tower = {
    ...towerWithState,
    barracksState: {
      ...barracksState,
      soldierIds: soldiers.map(s => s.id),
    },
  }

  return { tower: finalTower, soldiers }
}

/**
 * Calculate damage for a soldier attack
 */
export function calculateSoldierDamage(soldier: BarracksSoldier): number {
  // Apply shrine damage bonus
  const mult = 1 + soldier.shrineBuffs.damageBonus
  const min = Math.floor(soldier.damage.min * mult)
  const max = Math.floor(soldier.damage.max * mult)
  return Math.floor(min + Math.random() * (max - min + 1))
}

/**
 * Find the nearest enemy for a soldier to target
 */
export function findSoldierTarget(
  soldier: BarracksSoldier,
  enemies: Enemy[],
  searchRadius: number = 150, // Soldiers look for enemies within this range
): Enemy | null {
  let nearestEnemy: Enemy | null = null
  let minDistanceSquared = searchRadius * searchRadius

  for (const enemy of enemies) {
    if (enemy.health <= 0 || enemy.reachedEnd) continue

    const distSq = distanceSquared(soldier.position, enemy.position)
    if (distSq < minDistanceSquared) {
      minDistanceSquared = distSq
      nearestEnemy = enemy
    }
  }

  return nearestEnemy
}

/**
 * Get facing direction based on angle
 */
function getFacingDirection(angle: number): "up" | "down" | "side" {
  // Normalize angle to 0-2PI
  const normalized = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)

  // Up: roughly 225-315 degrees (pointing upward on screen)
  // Down: roughly 45-135 degrees (pointing downward on screen)
  // Side: everything else
  if (normalized > Math.PI * 0.75 && normalized < Math.PI * 1.25) {
    return "up"
  } else if (normalized < Math.PI * 0.25 || normalized > Math.PI * 1.75) {
    return "down"
  }
  return "side"
}

/**
 * Update a soldier's movement, targeting, and combat
 */
export function updateBarracksSoldier(
  soldier: BarracksSoldier,
  enemies: Enemy[],
  rallyPoint: Position,
  delta: number,
  barracksTowerLevel: number = 1,
  currentFrame: number = 0,
): { soldier: BarracksSoldier; hitEnemyId: string | null; damage: number } {
  // Don't update dead soldiers
  if (soldier.isDead) {
    return { soldier, hitEnemyId: null, damage: 0 }
  }

  const updated = { ...soldier }
  let hitEnemyId: string | null = null
  let damage = 0
  const now = gameNow()

  // Update attack cooldown
  if (updated.attackCooldown > 0) {
    updated.attackCooldown -= delta
  }

  // Apply health regeneration only if 2 seconds have passed since last damage
  // Regen rate is 5x the base config rate
  const config = getFootmanConfig(barracksTowerLevel)
  const timeSinceLastDamage = now - updated.lastDamageTime
  const REGEN_DELAY_MS = 2000 // 2 seconds delay after taking damage

  if (timeSinceLastDamage >= REGEN_DELAY_MS && updated.health < updated.maxHealth) {
    const regenPerFrame = (config.healthRegen * 5) / 60 // 5x rate, convert per-second to per-frame
    updated.health = Math.min(updated.maxHealth, updated.health + regenPerFrame * delta)
  }

  // Check if forced to rally (e.g., after rally point was moved)
  const isForceRallying = updated.forceRallyUntil > now

  // Find nearest enemy (only if not forced to rally)
  const nearestEnemy = isForceRallying ? null : findSoldierTarget(updated, enemies)

  if (nearestEnemy) {
    // Calculate distance to enemy
    const distSq = distanceSquared(updated.position, nearestEnemy.position)
    const attackRangeSq = updated.attackRange * updated.attackRange

    // Face the enemy
    const angle = calculateAngle(updated.position, nearestEnemy.position)
    updated.rotation = angle
    updated.facingDirection = getFacingDirection(angle)

    if (distSq > attackRangeSq) {
      // Move towards enemy (apply slow multiplier and shrine speed bonus)
      const effectiveSpeed = updated.speed * updated.slowMultiplier * (1 + updated.shrineBuffs.movementSpeedBonus)
      updated.position = moveTowards(
        updated.position,
        nearestEnemy.position,
        effectiveSpeed * delta,
      )
      updated.animationState = "walk"
      updated.target = nearestEnemy.id
    } else {
      // In attack range
      updated.animationState = "attack"
      updated.target = nearestEnemy.id

      // Attack if cooldown ready
      if (updated.attackCooldown <= 0) {
        hitEnemyId = nearestEnemy.id
        damage = calculateSoldierDamage(updated)
        // Use effective attack speed accounting for debuffs
        updated.attackCooldown = getEffectiveSoldierAttackSpeed(updated, currentFrame)
        updated.animationStartTime = now
      }
    }
  } else {
    // No enemy nearby - return to rally point
    updated.target = null

    const rallyOffsetX = updated.soldierIndex === 0 ? -15 : updated.soldierIndex === 1 ? 15 : 0
    const soldierRallyPos: Position = {
      x: rallyPoint.x + rallyOffsetX,
      y: rallyPoint.y,
    }

    const distToRallySq = distanceSquared(updated.position, soldierRallyPos)

    if (distToRallySq > 100) { // More than ~10px away
      // Walk back to rally point (apply slow multiplier and shrine speed bonus)
      const effectiveSpeed = updated.speed * updated.slowMultiplier * (1 + updated.shrineBuffs.movementSpeedBonus)
      updated.position = moveTowards(
        updated.position,
        soldierRallyPos,
        effectiveSpeed * delta,
      )
      const angle = calculateAngle(updated.position, soldierRallyPos)
      updated.rotation = angle
      updated.facingDirection = getFacingDirection(angle)
      updated.animationState = "walk"
    } else {
      // At rally point, idle
      updated.animationState = "idle"
      updated.facingDirection = "down"
    }
  }

  return { soldier: updated, hitEnemyId, damage }
}

/**
 * Apply damage to a soldier
 */
export function damageBarracksSoldier(
  soldier: BarracksSoldier,
  damage: number,
): BarracksSoldier {
  // Reduce damage by armor (1 armor = 1 damage reduction, minimum 1 damage)
  const actualDamage = Math.max(1, damage - soldier.armor)
  const newHealth = Math.max(0, soldier.health - actualDamage)

  return {
    ...soldier,
    health: newHealth,
    lastDamageTime: gameNow(), // Track when damage was received for regen delay
  }
}

/**
 * Mark a soldier as dead and set respawn timer
 */
export function handleSoldierDeath(soldier: BarracksSoldier): BarracksSoldier {
  const now = gameNow()
  return {
    ...soldier,
    isDead: true,
    health: 0,
    deathTime: now,
    respawnTime: now + RESPAWN_TIME_MS,
    animationState: "death",
    animationStartTime: now,
    target: null,
  }
}

/**
 * Check if a soldier is ready to respawn
 */
export function isSoldierReadyToRespawn(soldier: BarracksSoldier): boolean {
  if (!soldier.isDead || !soldier.respawnTime) return false
  return gameNow() >= soldier.respawnTime
}

/**
 * Get respawn progress for a dead soldier (0-1)
 */
export function getSoldierRespawnProgress(soldier: BarracksSoldier): number {
  if (!soldier.isDead || !soldier.deathTime || !soldier.respawnTime) return 1

  const now = gameNow()
  const elapsed = now - soldier.deathTime
  const total = soldier.respawnTime - soldier.deathTime

  return Math.min(1, elapsed / total)
}

/**
 * Respawn a soldier at the rally point
 */
export function respawnSoldier(
  soldier: BarracksSoldier,
  barracksTower: Tower,
  soldierHpModifier: number = 1,
): BarracksSoldier {
  const config = getFootmanConfig(barracksTower.level)
  const rallyPoint = barracksTower.barracksState?.rallyPoint ?? {
    x: barracksTower.position.x,
    y: barracksTower.position.y + DEFAULT_RALLY_OFFSET_Y,
  }

  const offsetX = soldier.soldierIndex === 0 ? -15 : soldier.soldierIndex === 1 ? 15 : 0
  const now = gameNow()

  // Apply HP modifier from Hardened Veterans upgrade
  const modifiedHealth = Math.floor(config.health * soldierHpModifier)

  return {
    ...soldier,
    level: barracksTower.level, // Update level in case barracks was upgraded
    position: {
      x: rallyPoint.x + offsetX,
      y: rallyPoint.y,
    },
    health: modifiedHealth,
    maxHealth: modifiedHealth,
    isDead: false,
    deathTime: null,
    respawnTime: null,
    spawnTime: now,
    lastDamageTime: 0, // Reset damage time on respawn
    animationState: "idle",
    animationStartTime: now,
    facingDirection: "down",
    target: null,
    attackCooldown: 0,
    // Reset debuffs on respawn
    attackSpeedDebuff: 1,
    attackSpeedDebuffEndFrame: 0,
    slowMultiplier: 1,
    slowEndFrame: 0,
    forceRallyUntil: 0, // Not forced to rally on respawn
  }
}

/**
 * Check all soldiers for respawns
 */
export function checkSoldierRespawns(
  soldiers: BarracksSoldier[],
  towers: Tower[],
  soldierHpModifier: number = 1,
): BarracksSoldier[] {
  return soldiers.map((soldier) => {
    if (!isSoldierReadyToRespawn(soldier)) return soldier

    // Find the barracks tower
    const barracksTower = towers.find((t) => t.id === soldier.barracksTowerId)
    if (!barracksTower) return soldier

    return respawnSoldier(soldier, barracksTower, soldierHpModifier)
  })
}

/**
 * Apply attack speed debuff to a soldier (from elite rat's disease carrier ability)
 */
export function applySoldierAttackSpeedDebuff(
  soldier: BarracksSoldier,
  debuffPercent: number,
  durationFrames: number,
  currentFrame: number
): BarracksSoldier {
  return {
    ...soldier,
    attackSpeedDebuff: 1 - debuffPercent, // e.g., 0.15 becomes 0.85 multiplier
    attackSpeedDebuffEndFrame: currentFrame + durationFrames,
  }
}

/**
 * Get the effective attack speed for a soldier, accounting for debuffs
 */
export function getEffectiveSoldierAttackSpeed(
  soldier: BarracksSoldier,
  currentFrame: number
): number {
  // If debuff is expired, return normal attack speed
  if (currentFrame >= soldier.attackSpeedDebuffEndFrame) {
    return soldier.attackSpeed
  }
  // Otherwise, apply the debuff (higher value = slower attacks)
  return Math.ceil(soldier.attackSpeed / soldier.attackSpeedDebuff)
}

/**
 * Update soldier debuffs (clear expired ones)
 */
export function updateSoldierDebuffs(
  soldier: BarracksSoldier,
  currentFrame: number
): BarracksSoldier {
  let updated = soldier
  let changed = false

  // Clear expired attack speed debuff
  if (currentFrame >= soldier.attackSpeedDebuffEndFrame && soldier.attackSpeedDebuff !== 1) {
    updated = {
      ...updated,
      attackSpeedDebuff: 1,
      attackSpeedDebuffEndFrame: 0,
    }
    changed = true
  }

  // Clear expired movement slow
  if (currentFrame >= soldier.slowEndFrame && soldier.slowMultiplier !== 1) {
    updated = {
      ...updated,
      slowMultiplier: 1,
      slowEndFrame: 0,
    }
    changed = true
  }

  return changed ? updated : soldier
}

/**
 * Set rally point for a barracks tower
 * Returns updated tower and whether soldiers should walk to new rally
 */
export function setRallyPoint(
  tower: Tower,
  position: Position,
): Tower {
  if (!tower.barracksState) return tower

  // Clamp position to within rally radius
  const dx = position.x - tower.position.x
  const dy = position.y - tower.position.y
  const dist = Math.sqrt(dx * dx + dy * dy)

  let finalPosition = position
  if (dist > RALLY_RADIUS) {
    // Clamp to radius
    const scale = RALLY_RADIUS / dist
    finalPosition = {
      x: tower.position.x + dx * scale,
      y: tower.position.y + dy * scale,
    }
  }

  return {
    ...tower,
    barracksState: {
      ...tower.barracksState,
      rallyPoint: finalPosition,
    },
  }
}

/**
 * Force soldiers of a barracks to stop attacking and move to rally point
 * Returns updated soldiers with forceRallyUntil set
 */
export function forceSoldiersToRally(
  soldiers: BarracksSoldier[],
  barracksTowerId: string,
  durationMs: number = 2000,
): BarracksSoldier[] {
  const forceRallyUntil = gameNow() + durationMs

  return soldiers.map((soldier) => {
    if (soldier.barracksTowerId !== barracksTowerId || soldier.isDead) return soldier

    return {
      ...soldier,
      target: null, // Clear current target
      forceRallyUntil,
    }
  })
}

/**
 * Upgrade soldiers when barracks is upgraded
 */
export function upgradeSoldiers(
  soldiers: BarracksSoldier[],
  barracksTowerId: string,
  newLevel: number,
): BarracksSoldier[] {
  const config = getFootmanConfig(newLevel)

  return soldiers.map((soldier) => {
    if (soldier.barracksTowerId !== barracksTowerId) return soldier

    // Calculate health percentage to maintain
    const healthPercent = soldier.health / soldier.maxHealth

    return {
      ...soldier,
      level: newLevel, // Update soldier level
      maxHealth: config.health,
      health: soldier.isDead ? 0 : Math.floor(config.health * healthPercent),
      damage: config.damage,
      armor: config.armor,
      speed: config.speed,
    }
  })
}

/**
 * Get soldiers belonging to a specific barracks tower
 */
export function getSoldiersForBarracks(
  soldiers: BarracksSoldier[],
  barracksTowerId: string,
): BarracksSoldier[] {
  return soldiers.filter((s) => s.barracksTowerId === barracksTowerId)
}

/**
 * Remove soldiers belonging to a specific barracks tower (when sold)
 */
export function removeSoldiersForBarracks(
  soldiers: BarracksSoldier[],
  barracksTowerId: string,
): BarracksSoldier[] {
  return soldiers.filter((s) => s.barracksTowerId !== barracksTowerId)
}

/**
 * Check if a soldier is dead (health <= 0)
 */
export function isSoldierDead(soldier: BarracksSoldier): boolean {
  return soldier.health <= 0 || soldier.isDead
}

/**
 * Get the current soldier name based on barracks level
 */
export function getSoldierName(barracksTower: Tower): string {
  return getFootmanConfig(barracksTower.level).name
}

/**
 * Check if a position is within rally point placement radius
 */
export function isWithinRallyRadius(
  towerPosition: Position,
  targetPosition: Position,
): boolean {
  const dx = targetPosition.x - towerPosition.x
  const dy = targetPosition.y - towerPosition.y
  const distSq = dx * dx + dy * dy
  return distSq <= RALLY_RADIUS * RALLY_RADIUS
}

/**
 * Get the rally radius constant
 */
export function getRallyRadius(): number {
  return RALLY_RADIUS
}

/**
 * Get effective soldier max health with shrine bonus
 */
export function getEffectiveSoldierMaxHealth(soldier: BarracksSoldier): number {
  return Math.floor(soldier.maxHealth * (1 + soldier.shrineBuffs.healthBonus))
}

/**
 * Get effective soldier damage with shrine bonus
 */
export function getEffectiveSoldierDamage(soldier: BarracksSoldier): number {
  const baseMin = soldier.damage.min
  const baseMax = soldier.damage.max
  const mult = 1 + soldier.shrineBuffs.damageBonus
  // Return average damage with bonus applied
  return Math.floor((baseMin + baseMax) / 2 * mult)
}
